import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Avatar,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  InputAdornment,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import { toast } from "../../services/toast";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const KINDS = [
  { value: "supabase", label: "Supabase" },
  { value: "rest", label: "API REST" },
  { value: "postgres", label: "PostgreSQL directo" },
  { value: "mongo", label: "MongoDB" },
];

const isDatabaseUri = (source) =>
  source?.kind === "postgres" || source?.kind === "mongo";

const URI_PLACEHOLDER = {
  postgres: "postgres://usuario:clave@host:5432/basededatos",
  mongo: "mongodb://usuario:clave@host:27017/basededatos",
};

const EMPTY = { name: "", kind: "supabase", baseUrl: "", headers: {} };

let rowSequence = 0;
const newRow = (name = "", value = "") => {
  rowSequence += 1;
  return { id: rowSequence, name, value, visible: !value };
};

const headersToRows = (headers) => Object.entries(headers || {}).map(([name, value]) => newRow(name, value));

const rowsToHeaders = (rows) =>
  rows.reduce((headers, row) => {
    const name = row.name.trim();
    if (name) headers[name] = row.value.trim();
    return headers;
  }, {});

const HeadersEditor = ({ rows, onChange, classes }) => {
  const update = (id, changes) => onChange(rows.map((row) => (row.id === id ? { ...row, ...changes } : row)));
  const remove = (id) => onChange(rows.filter((row) => row.id !== id));

  return (
    <div>
      <Typography className={classes.headersTitle}>Cabeceras</Typography>
      <div className={classes.headersList}>
        {rows.length === 0 && <Typography className={classes.headersEmpty}>Sin cabeceras.</Typography>}
        {rows.map((row) => (
          <div key={row.id} className={classes.headerRow}>
            <TextField
              size="small"
              placeholder="Nombre"
              value={row.name}
              onChange={(e) => update(row.id, { name: e.target.value })}
            />
            <TextField
              size="small"
              placeholder="Valor"
              type={row.visible ? "text" : "password"}
              value={row.value}
              onChange={(e) => update(row.id, { value: e.target.value })}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => update(row.id, { visible: !row.visible })}>
                        {row.visible ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <IconButton size="small" onClick={() => remove(row.id)} className={classes.headerDelete}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </div>
        ))}
      </div>
      <div className={classes.headerActions}>
        <Button size="small" startIcon={<AddOutlinedIcon />} onClick={() => onChange([...rows, newRow()])}>
          Agregar cabecera
        </Button>
      </div>
      <Typography className={classes.headersHelp}>
        Se guardan en el servidor; el agente nunca las ve.
      </Typography>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  headersTitle: {
    fontSize: 13,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  headersList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  headersEmpty: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  headerRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 2fr) minmax(0, 3fr) 34px",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  headerDelete: {
    color: theme.palette.text.disabled,
    "&:hover": { color: theme.palette.error.main },
  },
  headerActions: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  headersHelp: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: theme.spacing(2),
  },
  empty: {
    padding: theme.spacing(6),
    textAlign: "center",
    color: theme.palette.text.secondary,
    lineHeight: 1.7,
  },
  card: {
    display: "flex",
    flexDirection: "column",
  },
  cardContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    flex: 1,
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
  },
  avatar: {
    backgroundColor: theme.palette.mode === "light" ? "#eef0ff" : "rgba(255,255,255,0.08)",
    color: "#5b6cff",
  },
  name: {
    fontWeight: 600,
    lineHeight: 1.3,
  },
  url: {
    fontFamily: "ui-monospace, Consolas, monospace",
    fontSize: 12,
    lineHeight: 1.45,
    color: theme.palette.text.secondary,
    wordBreak: "break-all",
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: "auto",
    paddingTop: theme.spacing(0.5),
  },
  actions: {
    justifyContent: "flex-end",
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    paddingTop: theme.spacing(1),
    minWidth: 460,
  },
}));

const DataSourceCard = ({ source, classes, onEdit, onDelete }) => {
  const claves = Object.keys(source.headers || {});

  return (
    <Card variant="outlined" className={classes.card}>
      <CardContent className={classes.cardContent}>
        <div className={classes.cardHeader}>
          <Avatar className={classes.avatar}>
            <StorageOutlinedIcon />
          </Avatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography className={classes.name} noWrap>
              {source.name}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {KINDS.find((k) => k.value === source.kind)?.label || source.kind}
            </Typography>
          </div>
        </div>

        <Typography className={classes.url}>{source.baseUrl}</Typography>

        <div className={classes.chips}>
          {claves.length === 0 ? (
            <Chip size="small" variant="outlined" label="Sin credenciales" />
          ) : (
            claves.map((clave) => (
              <Chip key={clave} size="small" variant="outlined" label={clave} />
            ))
          )}
        </div>
      </CardContent>

      <CardActions className={classes.actions}>
        <IconButton size="small" onClick={() => onEdit({ ...source })}>
          <EditIcon color="secondary" fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => onDelete(source)}>
          <DeleteOutlineIcon color="secondary" fontSize="small" />
        </IconButton>
      </CardActions>
    </Card>
  );
};

const DataSources = () => {
  const { classes } = useStyles();

  const [dataSources, setDataSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [headerRows, setHeaderRows] = useState([]);
  const editingKey = editing ? editing.id ?? "new" : null;

  useEffect(() => {
    if (editingKey !== null) setHeaderRows(headersToRows(editing?.headers));
  }, [editingKey]);
  const [deleting, setDeleting] = useState(null);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/data-sources");
      setDataSources(data || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleSave = async () => {
    if (!editing?.name?.trim() || !editing?.baseUrl?.trim()) return;

    setSaving(true);
    const payload = { ...editing, headers: rowsToHeaders(headerRows) };
    try {
      if (editing.id) {
        await api.put(`/data-sources/${editing.id}`, payload);
      } else {
        await api.post("/data-sources", payload);
      }
      toast.success("Conexión guardada");
      setEditing(null);
      await reload();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/data-sources/${deleting.id}`);
      toast.success("Conexión eliminada");
      await reload();
    } catch (err) {
      toastError(err);
    }
    setDeleting(null);
  };

  return (
    <MainContainer maxWidth={false}>
      <ConfirmationModal
        title={deleting ? `¿Eliminar "${deleting.name}"?` : ""}
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      >
        Las herramientas que la usaban quedan sin conexión y dejan de funcionar hasta
        que les asignes otra. No se borran.
      </ConfirmationModal>

      <MainHeader>
        <Title>Conexiones de datos</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setEditing({ ...EMPTY })}
          >
            Agregar conexión
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className={classes.mainPaper}>
        {!loading && dataSources.length === 0 ? (
          <Typography className={classes.empty}>
            La URL y las credenciales de una base externa, escritas una sola vez.
            <br />
            Después, en Open.Ai → un prompt → Herramientas, elegís la conexión y la
            colección, sin volver a pegar la clave.
          </Typography>
        ) : (
          <div className={classes.grid}>
            {dataSources.map((source) => (
              <DataSourceCard
                key={source.id}
                source={source}
                classes={classes}
                onEdit={setEditing}
                onDelete={setDeleting}
              />
            ))}
          </div>
        )}
      </div>

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.id ? "Editar conexión" : "Nueva conexión"}</DialogTitle>
        <DialogContent dividers>
          <div className={classes.form}>
            <TextField
              label="Nombre"
              size="small"
              variant="outlined"
              fullWidth
              autoFocus
              value={editing?.name || ""}
              onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))}
            />
            <TextField
              select
              label="Tipo"
              size="small"
              variant="outlined"
              fullWidth
              value={editing?.kind || "supabase"}
              onChange={(e) => setEditing((s) => ({ ...s, kind: e.target.value }))}
            >
              {KINDS.map((kind) => (
                <MenuItem key={kind.value} value={kind.value}>
                  {kind.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={isDatabaseUri(editing) ? "URI de conexión" : "URL base"}
              size="small"
              variant="outlined"
              fullWidth
              placeholder={
                URI_PLACEHOLDER[editing?.kind] ||
                "https://tu-proyecto.supabase.co/rest/v1"
              }
              helperText={
                isDatabaseUri(editing)
                  ? "Con la base al final y un usuario de solo lectura: la clave queda guardada."
                  : "Sin la colección al final: esa la elige cada herramienta."
              }
              value={editing?.baseUrl || ""}
              onChange={(e) => setEditing((s) => ({ ...s, baseUrl: e.target.value }))}
            />
            {!isDatabaseUri(editing) && (
              <HeadersEditor rows={headerRows} onChange={setHeaderRows} classes={classes} />
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={saving || !editing?.name?.trim() || !editing?.baseUrl?.trim()}
            onClick={handleSave}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default DataSources;
